import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Bar,
  BarChart,
  Cell,
  LabelList,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

/* Whole Foods USA compliance card. Sits below the Golden Ratio card on the
   formula sheet. One button-triggered call to /api/wfm-compliance (Kimi K3);
   the result is cached in localStorage per formula composition, so re-renders
   and slider moves never re-bill. This is retailer compliance against the
   vendored WFM banned list — not the ultra-processed-food score. */

export interface WfmItem {
  commonName: string;
  fdaName: string | null;
  pct: number;
}

interface WfmScore {
  commonName: string;
  declaredAs?: string;
  abbrev: string;
  score: number;
  rationale: string;
  shelfEvidence?: {
    tier1: number;
    tier2: number;
    examples: string[];
    matchedTerm?: string | null;
    searchedTerms?: string[];
  } | null;
}

interface WfmResult {
  statement: string;
  /** true when this came from the shared database row rather than a fresh run */
  cached?: boolean;
  cachedAt?: string;
  containsStatement?: string;
  advisories?: string[];
  openIssues?: Array<{ title: string; detail: string }>;
  ingredients: WfmScore[];
  listSource?: string;
  listSnapshot?: string;
  model?: string;
  checkedAt?: string;
}

const CACHE_PREFIX = "wfm-compliance:";

/* Feature is live as EXPERIMENTAL (Pal's call, 2026-08-19): runs enabled,
   results shown at full strength, one short disclaimer ("Experimental
   feature — may be wrong."). The declaration-logic caveats (water, natural
   flavors, compound ingredients) still stand; the rebuild into a real
   regulatory framework is still planned. Flip to true to re-disable runs
   and bring back the under-construction banner/watermark. */
const UNDER_CONSTRUCTION = false;

function compositionHash(items: WfmItem[]): string {
  const src = items
    .map((i) => i.commonName + "|" + (i.fdaName || "") + "|" + i.pct.toFixed(2))
    .join(";");
  let h = 5381;
  for (let i = 0; i < src.length; i++) {
    h = ((h << 5) + h + src.charCodeAt(i)) | 0;
  }
  return String(h >>> 0);
}

function clampScore(n: number): number {
  if (typeof n !== "number" || isNaN(n)) return 0;
  return Math.max(-100, Math.min(100, Math.round(n)));
}

/* Diverging fill: polarity by hue, magnitude by lightness. Direction and bar
   length carry the same information, so color is reinforcement, not the only
   channel. Pale steps get a pole-colored stroke for relief on the white card. */
function barStyle(score: number): { fill: string; stroke: string; ink: string } {
  if (score >= 90) return { fill: "#15803d", stroke: "none", ink: "#ffffff" };
  if (score >= 50) return { fill: "#4ade80", stroke: "none", ink: "#14532d" };
  if (score >= 1) return { fill: "#86efac", stroke: "#16a34a", ink: "#14532d" };
  if (score === 0) return { fill: "#9ca3af", stroke: "none", ink: "#1f2937" };
  if (score > -50) return { fill: "#fca5a5", stroke: "#b91c1c", ink: "#450a0a" };
  if (score > -90) return { fill: "#f87171", stroke: "none", ink: "#450a0a" };
  return { fill: "#b91c1c", stroke: "none", ink: "#ffffff" };
}

/* 2-letter code inside the bar when it fits, just past its end when it does not. */
const AbbrevLabel = (props: any) => {
  const { x, y, width, height, value, index, data } = props;
  if (x == null || width == null) return null;
  const score: number = data && data[index] ? data[index].score : 0;
  const style = barStyle(score);
  const fits = Math.abs(height) >= 14;
  const cx = x + width / 2;
  let cy: number;
  let ink: string;
  if (fits) {
    cy = y + height / 2 + 3;
    ink = style.ink;
  } else if (score >= 0) {
    cy = y - 4;
    ink = "#6b7280";
  } else {
    cy = y + height + 10;
    ink = "#6b7280";
  }
  return (
    <text
      x={cx}
      y={cy}
      textAnchor="middle"
      fontSize={7}
      fontWeight={700}
      fill={ink}
      style={{ letterSpacing: "0.5px", pointerEvents: "none" }}
    >
      {value}
    </text>
  );
};

const ScoreTooltip = ({ active, payload }: any) => {
  if (!active || !payload || !payload.length) return null;
  const d = payload[0].payload;
  return (
    <div
      style={{
        background: "#ffffff",
        border: "1px solid #e5e7eb",
        borderRadius: "6px",
        boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
        padding: "8px 10px",
        maxWidth: "260px",
        fontSize: "11px",
        color: "#1f2937",
      }}
    >
      <div style={{ fontWeight: 700, color: "#1e1b4b" }}>{d.commonName}</div>
      {d.declaredAs && d.declaredAs !== d.commonName && (
        <div style={{ color: "#6b7280" }}>Declared as: {d.declaredAs}</div>
      )}
      <div style={{ margin: "3px 0", fontWeight: 600, color: barStyle(d.score).fill === "#86efac" || barStyle(d.score).fill === "#fca5a5" ? (barStyle(d.score).stroke as string) : barStyle(d.score).fill }}>
        Score: {d.score > 0 ? "+" + d.score : d.score} · {d.pct.toFixed(1)}% of formula
      </div>
      <div style={{ color: "#4b5563", lineHeight: 1.35 }}>{d.rationale}</div>
      {d.shelfEvidence && (
        <div style={{ marginTop: "3px", color: "#6b7280", fontSize: "10px" }}>
          {d.shelfEvidence.tier1 + d.shelfEvidence.tier2 > 0
            ? `On WF shelves: ${d.shelfEvidence.tier1} confirmed + ${d.shelfEvidence.tier2} same-brand products` +
              (d.shelfEvidence.matchedTerm &&
              d.shelfEvidence.matchedTerm.toLowerCase() !== d.commonName.toLowerCase()
                ? ` — matched as "${d.shelfEvidence.matchedTerm}"`
                : "") +
              (d.shelfEvidence.examples && d.shelfEvidence.examples.length
                ? ` (${d.shelfEvidence.examples.join(", ")})`
                : "")
            : "No shelf evidence in the 21k-product corpus — high risk" +
              (d.shelfEvidence.searchedTerms && d.shelfEvidence.searchedTerms.length
                ? ` (searched: ${d.shelfEvidence.searchedTerms.join(", ")})`
                : "")}
        </div>
      )}
    </div>
  );
};

const WholeFoodsComplianceCard: React.FC<{
  items: WfmItem[];
  formulaCode: string;
}> = ({ items, formulaCode }) => {
  const { t } = useTranslation();
  const [result, setResult] = useState<WfmResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [lookingUp, setLookingUp] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    if (!loading) return;
    setElapsed(0);
    const id = window.setInterval(() => setElapsed((s) => s + 1), 1000);
    return () => window.clearInterval(id);
  }, [loading]);

  const hash = useMemo(() => compositionHash(items), [items]);
  // Always-current hash for the async runCheck closure: the run takes 2-4 min,
  // and the composition may have been edited (and the result reset) meanwhile.
  const hashRef = useRef(hash);
  hashRef.current = hash;

  /* On open: paint instantly from this browser's localStorage, then ask the
     server for the shared database row, which wins if it exists. The lookup
     never runs the model, so opening a formula is always free. */
  useEffect(() => {
    setResult(null);
    setError(null);
    try {
      const raw = localStorage.getItem(CACHE_PREFIX + hash);
      if (raw) setResult(JSON.parse(raw));
    } catch (e) {
      /* cache miss is fine */
    }

    /* nothing is displayed while under construction — do not even look it up */
    if (UNDER_CONSTRUCTION || items.length === 0) return;
    let cancelled = false;
    (async () => {
      setLookingUp(true);
      try {
        const resp = await fetch("/api/wfm-compliance", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ingredients: items, formulaCode, mode: "lookup" }),
        });
        if (!resp.ok) return;
        const data = await resp.json();
        if (cancelled || !data || !data.cached || !data.statement) return;
        setResult(data);
        try {
          localStorage.setItem(CACHE_PREFIX + hash, JSON.stringify(data));
        } catch (e) {
          /* storage full — result still shown */
        }
      } catch (e) {
        /* offline or route down — localStorage copy still stands */
      } finally {
        if (!cancelled) setLookingUp(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    /* keyed on hash alone: `items` is a fresh array every render, and the hash
       already changes whenever the composition does */
  }, [hash]);

  const runCheck = async () => {
    const runHash = hash;
    setLoading(true);
    setError(null);
    try {
      const resp = await fetch("/api/wfm-compliance", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        // "run" skips the cache and overwrites the stored row.
        body: JSON.stringify({ ingredients: items, formulaCode, mode: "run" }),
      });
      let data: any = null;
      try {
        data = await resp.json();
      } catch (parseErr) {
        // 502/504 gateway pages are not JSON — give a human message instead
        // of "Unexpected token".
        throw new Error(
          `The AI check timed out on the server (HTTP ${resp.status}). Hit Re-check to try again — a retry usually completes.`
        );
      }
      if (!resp.ok) throw new Error(data.error || `Request failed (HTTP ${resp.status})`);
      const cleaned: WfmResult = {
        ...data,
        checkedAt: new Date().toISOString().slice(0, 10),
        ingredients: (data.ingredients || []).map((s: any) => ({
          commonName: String(s.commonName || ""),
          declaredAs: s.declaredAs,
          abbrev: String(s.abbrev || "??").replace(/[^A-Za-z]/g, "").slice(0, 2).toUpperCase() || "??",
          score: clampScore(s.score),
          rationale: String(s.rationale || ""),
          shelfEvidence: s.shelfEvidence || null,
        })),
      };
      // The composition changed while the AI run was in flight — this answer
      // belongs to the old composition, and the hash effect has already reset
      // the card. Painting it would show the wrong formula's verdict.
      if (hashRef.current !== runHash) return;
      setResult(cleaned);
      try {
        localStorage.setItem(CACHE_PREFIX + runHash, JSON.stringify(cleaned));
      } catch (e) {
        /* storage full — result still shown */
      }
    } catch (e: any) {
      setError(e.message || "Compliance check failed");
    } finally {
      setLoading(false);
    }
  };

  const chartData = useMemo(() => {
    if (!result) return [];
    const byName: { [k: string]: WfmScore } = {};
    result.ingredients.forEach((s) => {
      byName[s.commonName] = s;
    });
    /* input order = descending % of formula: greatest to least, as displayed */
    return items
      .map((i) => {
        const s = byName[i.commonName];
        if (!s) return null;
        return {
          commonName: i.commonName,
          declaredAs: s.declaredAs,
          abbrev: s.abbrev,
          score: s.score,
          rationale: s.rationale,
          shelfEvidence: s.shelfEvidence,
          pct: i.pct,
        };
      })
      .filter((d) => d !== null) as Array<WfmScore & { pct: number }>;
  }, [result, items]);

  const bannedCount = chartData.filter((d) => d.score <= -90).length;

  return (
    <div className="card" style={{ maxWidth: "1500px", margin: "20px auto" }}>
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          gap: "12px",
          flexWrap: "wrap",
          marginBottom: "10px",
        }}
      >
        <h3 style={{ fontSize: "12px", fontWeight: 700, color: "#1e1b4b", margin: 0 }}>
          {t("wfmCompliance.title", { defaultValue: "Whole Foods Compliance (US)" })}
          {!UNDER_CONSTRUCTION && result && bannedCount > 0 && (
            <span
              style={{
                marginLeft: "8px",
                fontSize: "10px",
                fontWeight: 600,
                color: "#991b1b",
                background: "#fee2e2",
                borderRadius: "999px",
                padding: "2px 8px",
              }}
            >
              {t("wfmCompliance.bannedBadge", {
                defaultValue: `${bannedCount} banned`,
                count: bannedCount,
              })}
            </span>
          )}
        </h3>
        <button
          type="button"
          onClick={runCheck}
          disabled={loading || items.length === 0 || UNDER_CONSTRUCTION}
          title={
            UNDER_CONSTRUCTION
              ? "Disabled while this feature is under construction"
              : undefined
          }
          style={{
            background: "none",
            border: `1px solid ${UNDER_CONSTRUCTION ? "#d1d5db" : "#7c3aed"}`,
            borderRadius: "6px",
            color: UNDER_CONSTRUCTION ? "#9ca3af" : "#7c3aed",
            fontSize: "11px",
            fontWeight: 600,
            cursor: UNDER_CONSTRUCTION ? "not-allowed" : loading ? "wait" : "pointer",
            padding: "3px 10px",
          }}
        >
          {loading
            ? t("wfmCompliance.checking", {
                defaultValue: `Researching declarations + checking... ${elapsed}s (takes 2-4 min)`,
                count: elapsed,
              })
            : result
            ? t("wfmCompliance.rerun", { defaultValue: "Run AI again (overwrites saved answer)" })
            : t("wfmCompliance.check", { defaultValue: "Check compliance (AI)" })}
        </button>
      </div>

      {UNDER_CONSTRUCTION && (
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: "8px",
            padding: "8px 10px",
            marginBottom: "10px",
            background: "repeating-linear-gradient(45deg, #fffbeb, #fffbeb 10px, #fef3c7 10px, #fef3c7 20px)",
            border: "1px solid #fcd34d",
            borderRadius: "6px",
          }}
        >
          <span style={{ fontSize: "11px", fontWeight: 700, color: "#92400e", whiteSpace: "nowrap" }}>
            {t("wfmCompliance.ucTag", { defaultValue: "FEATURE UNDER CONSTRUCTION" })}
          </span>
          <span style={{ fontSize: "10px", color: "#78350f", lineHeight: 1.4 }}>
            {t("wfmCompliance.ucBody", {
              defaultValue:
                "Do not use for labeling decisions. The declaration logic is being rebuilt to work from the raw ingredients as mixed, with proper handling of water, natural flavors and compound ingredients. Running new checks is disabled.",
            })}
          </span>
        </div>
      )}

      {error && (
        <p style={{ fontSize: "11px", color: "#b91c1c", margin: "0 0 8px 0" }}>{error}</p>
      )}

      {!UNDER_CONSTRUCTION && loading && (
        <p style={{ fontSize: "11px", color: "#6b7280", margin: "0 0 8px 0" }}>
          {t("wfmCompliance.stayWarn", {
            defaultValue:
              "This takes a few minutes. You can leave this screen — the answer is saved to the database when it finishes, and will be here when you come back.",
          })}
        </p>
      )}

      {!UNDER_CONSTRUCTION && !result && !loading && !error && (
        <p style={{ fontSize: "11px", color: "#6b7280", margin: 0 }}>
          {lookingUp
            ? t("wfmCompliance.lookingUp", { defaultValue: "Checking for a saved answer..." })
            : t("wfmCompliance.idle", {
                defaultValue:
                  "No saved answer for this formula yet. AI researches how each ingredient is declared on a US label (web research for trade names like RS4), then checks the declared names against the Whole Foods Market banned list and shelf corpus and writes a compliant ingredient statement. The answer is saved to the database, so it is paid for once.",
              })}
        </p>
      )}

      {!UNDER_CONSTRUCTION && result && result.cached && (
        <p style={{ fontSize: "10px", color: "#6b7280", margin: "0 0 8px 0" }}>
          {t("wfmCompliance.fromDb", {
            date: result.cachedAt ? ", " + String(result.cachedAt).slice(0, 10) : "",
            defaultValue: `Saved answer from the database${
              result.cachedAt ? ", " + String(result.cachedAt).slice(0, 10) : ""
            }. Nothing was spent to show this.`,
          })}
        </p>
      )}

      {!UNDER_CONSTRUCTION && result && (
        <div
          style={{
            display: "flex",
            gap: "20px",
            flexWrap: "wrap",
            position: "relative",
            /* readable, but visibly not a finished answer */
            opacity: UNDER_CONSTRUCTION ? 0.55 : 1,
            filter: UNDER_CONSTRUCTION ? "grayscale(0.35)" : "none",
          }}
        >
          {UNDER_CONSTRUCTION && (
            <div
              aria-hidden="true"
              style={{
                position: "absolute",
                inset: 0,
                zIndex: 2,
                pointerEvents: "none",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                overflow: "hidden",
              }}
            >
              <span
                style={{
                  transform: "rotate(-18deg)",
                  fontSize: "26px",
                  fontWeight: 800,
                  letterSpacing: "3px",
                  color: "rgba(146, 64, 14, 0.16)",
                  border: "3px solid rgba(146, 64, 14, 0.16)",
                  borderRadius: "10px",
                  padding: "6px 18px",
                  whiteSpace: "nowrap",
                  textTransform: "uppercase",
                }}
              >
                {t("wfmCompliance.ucWatermark", { defaultValue: "Under construction" })}
              </span>
            </div>
          )}
          <div style={{ flex: "1 1 340px", minWidth: "300px" }}>
            <div
              style={{
                fontSize: "10px",
                fontWeight: 700,
                color: "#6b7280",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                marginBottom: "4px",
              }}
            >
              {t("wfmCompliance.statementHeading", { defaultValue: "Smart ingredient statement" })}
            </div>
            <p
              style={{
                fontSize: "11px",
                color: "#1f2937",
                lineHeight: 1.5,
                margin: 0,
                background: "#f9fafb",
                border: "1px solid #e5e7eb",
                borderRadius: "6px",
                padding: "10px",
              }}
            >
              {result.statement}
              {result.containsStatement ? (
                <>
                  <br />
                  <strong>{result.containsStatement}</strong>
                </>
              ) : null}
            </p>
            {result.advisories && result.advisories.length > 0 && (
              <p style={{ fontSize: "10px", color: "#4b5563", margin: "6px 0 0", fontStyle: "italic" }}>
                {result.advisories.map((a, i) => (
                  <span key={i}>
                    {a}
                    {i < result.advisories!.length - 1 ? <br /> : null}
                  </span>
                ))}
              </p>
            )}
            {result.openIssues && result.openIssues.length > 0 && (
              <div
                style={{
                  marginTop: "8px",
                  padding: "8px 10px",
                  background: "#fffbeb",
                  border: "1px solid #fde68a",
                  borderRadius: "6px",
                }}
              >
                <div style={{ fontSize: "10px", fontWeight: 700, color: "#92400e", marginBottom: "4px" }}>
                  {t("wfmCompliance.openIssues", {
                    defaultValue: `Before label sign-off (${result.openIssues.length})`,
                    count: result.openIssues.length,
                  })}
                </div>
                <ol style={{ margin: 0, paddingLeft: "16px", fontSize: "10px", color: "#78350f", lineHeight: 1.45 }}>
                  {result.openIssues.map((o, i) => (
                    <li key={i}>
                      <strong>{o.title}</strong>
                      {o.detail ? ` — ${o.detail}` : ""}
                    </li>
                  ))}
                </ol>
              </div>
            )}
            <p style={{ fontSize: "9px", color: "#9ca3af", marginTop: "6px", fontStyle: "italic" }}>
              {t("wfmCompliance.disclaimer", {
                defaultValue: "Experimental feature — may be wrong.",
              })}
            </p>
          </div>

          <div style={{ flex: "1 1 420px", minWidth: "320px" }}>
            <div
              style={{
                fontSize: "10px",
                fontWeight: 700,
                color: "#6b7280",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                marginBottom: "4px",
              }}
            >
              {t("wfmCompliance.chartHeading", {
                defaultValue: "Compliance by ingredient (largest first) — hover for detail",
              })}
            </div>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={chartData} margin={{ top: 14, right: 4, bottom: 14, left: 4 }}>
                <XAxis dataKey="abbrev" tick={false} axisLine={{ stroke: "#e5e7eb" }} tickLine={false} height={4} />
                <YAxis
                  domain={[-100, 100]}
                  ticks={[-100, -50, 0, 50, 100]}
                  tick={{ fontSize: 9, fill: "#9ca3af" }}
                  axisLine={false}
                  tickLine={false}
                  width={28}
                />
                <ReferenceLine y={0} stroke="#d1d5db" />
                <Tooltip content={<ScoreTooltip />} cursor={{ fill: "rgba(0,0,0,0.04)" }} />
                <Bar dataKey="score" radius={2} maxBarSize={34} isAnimationActive={false}>
                  {chartData.map((d, i) => {
                    const s = barStyle(d.score);
                    return (
                      <Cell
                        key={d.commonName}
                        fill={s.fill}
                        stroke={s.stroke === "none" ? undefined : s.stroke}
                        strokeWidth={s.stroke === "none" ? 0 : 1}
                      />
                    );
                  })}
                  <LabelList dataKey="abbrev" content={<AbbrevLabel data={chartData} />} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
};

export default WholeFoodsComplianceCard;
